import { useGaiaToast } from '@/composables/useGaiaToast';

export interface UseCopyToClipboardOptions {
  /** Toast shown once the text is on the clipboard. */
  successMessage?: string;
  /** Toast shown when the browser won't hand us a clipboard. */
  unavailableMessage?: string;
}

export interface UseCopyToClipboardReturn {
  /** Resolves `true` when the text was copied, `false` when it wasn't. */
  copy: (text: string) => Promise<boolean>;
}

/**
 * Copy text and say so.
 *
 * The guard is the point: `lib.dom` types `navigator.clipboard` as always
 * present, but it is missing in insecure contexts (plain http, some embedded
 * webviews), so a bare `await navigator.clipboard.writeText(…)` throws there
 * with nothing on screen to explain it. The `in` check keeps the runtime guard
 * without lying to TypeScript.
 *
 * Callable from anywhere — the toast API is module-scoped, so this no longer
 * needs a component instance.
 */
export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturn {
  const message = useGaiaToast();
  const successMessage = options.successMessage ?? 'Copied to clipboard';
  const unavailableMessage =
    options.unavailableMessage ?? 'Clipboard is not available in this browser';

  async function copy(text: string): Promise<boolean> {
    if (!('clipboard' in navigator)) {
      message.error(unavailableMessage);
      return false;
    }

    try {
      await navigator.clipboard.writeText(text);
      message.success(successMessage);
      return true;
    } catch {
      // A permissions refusal reads the same to the user as no clipboard at
      // all — either way the text did not get copied and they need to know.
      message.error(unavailableMessage);
      return false;
    }
  }

  return { copy };
}
